import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as services from "../services/saleng";
import { getContrastColor } from "../utils/fontColor";

const sortByTitle = (a, b) =>
  String(a.title ?? "").toLowerCase().localeCompare(String(b.title ?? "").toLowerCase());

const sortByCategoryThenTitle = (a, b) => {
  const categoryCompare = String(a.categoryName ?? "ไม่ระบุ").localeCompare(
    String(b.categoryName ?? "ไม่ระบุ"),
    "th"
  );

  if (categoryCompare !== 0) return categoryCompare;

  return sortByTitle(a, b);
};

const isSpacerItem = (item) => Boolean(item?.type === "spacer");

const createSpacerItem = (key) => ({
  type: "spacer",
  spacerId: `spacer-${key}`,
});

function ProductCard({
  item,
  boxName,
  isSelected,
  isDragging,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onLostPointerCapture,
  onClick,
  onRemove,
}) {
  if (isSpacerItem(item)) {
    return (
      <div
        data-drop-product-id={item.spacerId}
        data-drop-box={boxName}
        onPointerDown={(e) => onPointerDown(e, item, boxName)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onLostPointerCapture={onLostPointerCapture}
        onClick={onClick}
        style={{
          height: "100px",
          borderRadius: "6px",
          background: "#9ca3af",
          border: "2px dashed #cbd5e1",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
          opacity: isDragging ? 0.5 : 1,
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "pan-y",
          userSelect: "none",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#f8fafc",
          fontWeight: 700,
          flexDirection: "column",
          gap: "6px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "13px" }}>ช่องว่าง</div>
        {boxName === "box1" && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.spacerId);
            }}
            style={{
              padding: "4px 8px",
              border: "none",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.2)",
              color: "white",
              fontSize: "12px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            ลบ
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      id={item.productId}
      style={{
        padding: "12px",
        borderRadius: "6px",
        background: item.color,
        color: getContrastColor(item.color),
        cursor: isDragging ? "grabbing" : "grab",
        border: isSelected ? "3px solid yellow" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        height: "100px",
        textAlign: "center",
        flexDirection: "column",
        gap: "4px",
        opacity: isDragging ? 0.35 : 1,
        touchAction: "pan-y",
        userSelect: "none",
        position: "relative",
      }}
      onPointerDown={(e) => onPointerDown(e, item, boxName)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
      onClick={onClick}
    >
      <div>{item.title}</div>
      <div style={{ fontSize: "12px", fontWeight: 500, opacity: 0.9 }}>
        {item.code ? `รหัสสินค้า: ${item.code}` : " "}
      </div>
      {boxName === "box1" && isSelected && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.productId);
          }}
          style={{
            position: "absolute",
            top: "6px",
            right: "6px",
            padding: "5px 9px",
            border: "none",
            borderRadius: "6px",
            background: "#dc2626",
            color: "white",
            fontSize: "12px",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
          }}
        >
          เอาออก
        </button>
      )}
    </div>
  );
}

export default function ArrangeProducts() {
  const [box1, setBox1] = useState([]);
  const [box2, setBox2] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [draggedCard, setDraggedCard] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [touchPreview, setTouchPreview] = useState(null);
  const pointerDragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const pointerUpHandlerRef = useRef(null);
  const pointerCancelHandlerRef = useRef(null);
  const spacerSeqRef = useRef(0);

  const buildDefaultLayout = useCallback((items) => {
    return items.map((item, index) => ({
      productId: item.productId,
      box: "box1",
      position: index,
    }));
  }, []);

  const fetchData = useCallback(async () => {
    let allProducts = [];

    try {
      const productsRes = await services.getProductsBranch(true);
      const products = (productsRes?.data?.data ?? []).filter(
        (item) => !item.deletedAt
      );

      const productMap = {};
      products.forEach((item) => {
        productMap[item.id] = {
          productId: item.id,
          title: item.title,
          code: item.code ?? "",
          categoryId: item.category?.id ?? null,
          categoryName: item.category?.name ?? "ไม่ระบุ",
          color: item.subCategory?.color ?? "royalblue",
        };
      });

      allProducts = Object.values(productMap).sort(sortByCategoryThenTitle);
      const layoutRes = await services.getUserLayout();
      const layout = Array.isArray(layoutRes?.data) ? layoutRes.data : [];

      if (!layout.length) {
        const defaultBox1 = [...allProducts];
        setBox1(defaultBox1);
        setBox2([]);

        await services.saveLayout({
          layouts: buildDefaultLayout(defaultBox1),
        });
        return;
      }

      const laidOutIds = new Set(layout.map((item) => item.productId));
      const box1Arr = [];
      const box2Arr = [];
      let maxBox1Position = -1;

      layout.forEach((item) => {
        const card = productMap[item.productId];
        if (!card) return;

        if (item.box === "box1") {
          box1Arr.push({ ...card, position: item.position });
          maxBox1Position = Math.max(maxBox1Position, Number(item.position ?? 0));
        }

        if (item.box === "box2") {
          box2Arr.push({ ...card, position: item.position });
        }
      });

      const box1ByPosition = new Map(
        box1Arr.map((item) => [Number(item.position), item])
      );
      const box1Filled = Array.from({ length: maxBox1Position + 1 }, (_, index) => {
        const found = box1ByPosition.get(index);
        return found ?? createSpacerItem(index);
      }).filter(Boolean);

      const unplacedArr = allProducts
        .filter((item) => !laidOutIds.has(item.productId))
        .sort(sortByCategoryThenTitle);

      setBox1(box1Filled);
      setBox2([
        ...box2Arr.sort((a, b) => a.position - b.position).sort(sortByCategoryThenTitle),
        ...unplacedArr,
      ]);
    } catch (err) {
      console.error("Cannot load products/layout:", err);
      setBox1([]);
      setBox2(allProducts.sort(sortByCategoryThenTitle));
    }
  }, [buildDefaultLayout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const matchesSearch = useCallback(
    (item) => {
      if (isSpacerItem(item)) return true;

      const q = searchText.trim().toLowerCase();
      if (!q) return true;

      const title = String(item.title ?? "").toLowerCase();
      const code = String(item.code ?? "").toLowerCase();
      const category = String(item.categoryName ?? "").toLowerCase();

      return title.includes(q) || code.includes(q) || category.includes(q);
    },
    [searchText]
  );

  const filteredBox1 = useMemo(() => box1.filter(matchesSearch), [box1, matchesSearch]);
  const filteredBox2 = useMemo(() => box2.filter(matchesSearch), [box2, matchesSearch]);
  const availableCategories = useMemo(() => {
    const categories = new Map();

    [...box1, ...box2].forEach((item) => {
      const key = item.categoryName || "ไม่ระบุ";
      if (!categories.has(key)) {
        categories.set(key, key);
      }
    });

    return ["all", ...Array.from(categories.keys()).sort((a, b) => a.localeCompare(b, "th"))];
  }, [box1, box2]);

  const matchesCategory = useCallback(
    (item) => {
      if (isSpacerItem(item)) return true;

      if (selectedCategory === "all") return true;
      return String(item.categoryName ?? "ไม่ระบุ") === selectedCategory;
    },
    [selectedCategory]
  );

  const visibleBox1 = useMemo(
    () => filteredBox1.filter(matchesCategory),
    [filteredBox1, matchesCategory]
  );
  const visibleBox2 = filteredBox2;

  const saveLayoutToAPI = async () => {
    try {
      const layout = [
        ...box1.flatMap((item, index) =>
          isSpacerItem(item)
            ? []
            : [
                {
                  productId: item.productId,
                  box: "box1",
                  position: index,
                },
              ]
        ),
        ...box2.map((item, index) => ({
          productId: item.productId,
          box: "box2",
          position: index,
        })),
      ];

      await services.saveLayout({ layouts: layout });
      alert("บันทึกเรียบร้อยแล้ว!");
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  const handleCardClick = (id, boxName) => {
    const clickedItem = getBoxItems(boxName).find(
      (item) => String(item.productId ?? item.spacerId) === String(id)
    );
    if (isSpacerItem(clickedItem)) return;

    if (!selectedCard) {
      setSelectedCard({ id, boxName });
      return;
    }

    const { id: selectedId, boxName: selectedBox } = selectedCard;

    if (id === selectedId && boxName === selectedBox) {
      setSelectedCard(null);
      return;
    }

    if (boxName === selectedBox) {
      const setter = boxName === "box1" ? setBox1 : setBox2;
      const items = boxName === "box1" ? box1 : box2;
      const newItems = [...items];
      const i1 = newItems.findIndex(
        (item) => String(item.productId ?? item.spacerId) === String(id)
      );
      const i2 = newItems.findIndex(
        (item) => String(item.productId ?? item.spacerId) === String(selectedId)
      );
      [newItems[i1], newItems[i2]] = [newItems[i2], newItems[i1]];
      setter(newItems);
    } else {
      const fromSetter = selectedBox === "box1" ? setBox1 : setBox2;
      const toSetter = boxName === "box1" ? setBox1 : setBox2;
      const fromItems = selectedBox === "box1" ? [...box1] : [...box2];
      const toItems = boxName === "box1" ? [...box1] : [...box2];
      const movingItem = fromItems.find(
        (item) => String(item.productId ?? item.spacerId) === String(selectedId)
      );

      fromSetter(
        fromItems.filter(
          (item) => String(item.productId ?? item.spacerId) !== String(selectedId)
        )
      );
      if (movingItem) {
        toSetter([...toItems, movingItem]);
      }
    }

    setSelectedCard(null);
  };

  const handleRemoveCard = (id) => {
    const movingItem = box1.find(
      (item) => String(item.productId ?? item.spacerId) === String(id)
    );
    if (!movingItem) return;

    if (isSpacerItem(movingItem)) {
      setBox1((items) =>
        items.filter((item) => String(item.productId ?? item.spacerId) !== String(id))
      );
      setSelectedCard(null);
      return;
    }

    setBox1((items) =>
      items.filter((item) => String(item.productId ?? item.spacerId) !== String(id))
    );
    setBox2((items) => [
      ...items.filter((item) => String(item.productId ?? item.spacerId) !== String(id)),
      movingItem,
    ]);
    setSelectedCard(null);
  };

  const getBoxItems = (boxName) => (boxName === "box1" ? box1 : box2);

  const setBoxItems = (boxName, items) => {
    if (boxName === "box1") {
      setBox1(items);
    } else {
      setBox2(items);
    }
  };

  const moveCard = (id, sourceBox, targetBox, targetId = null, position = "after") => {
    const sourceItems = getBoxItems(sourceBox);
    const movingItem = sourceItems.find((item) => String(item.productId ?? item.spacerId) === id);
    if (!movingItem) return;

    if (isSpacerItem(movingItem) && targetBox !== "box1") {
      return;
    }

    const nextSourceItems = sourceItems.filter(
      (item) => String(item.productId ?? item.spacerId) !== id
    );
    const targetItems =
      sourceBox === targetBox
        ? nextSourceItems
        : getBoxItems(targetBox).filter(
            (item) => String(item.productId ?? item.spacerId) !== id
          );

    let insertIndex = targetItems.length;
    let isReplaceTarget = false;
    if (targetId !== null) {
      const targetIndex = targetItems.findIndex(
        (item) => String(item.productId ?? item.spacerId) === String(targetId)
      );
      if (targetIndex >= 0) {
        const targetItem = targetItems[targetIndex];
        if (isSpacerItem(targetItem) || position === "replace") {
          insertIndex = targetIndex;
          isReplaceTarget = true;
        } else {
          insertIndex = targetIndex + (position === "after" ? 1 : 0);
        }
      }
    }

    const nextTargetItems = [...targetItems];
    if (isReplaceTarget) {
      nextTargetItems.splice(insertIndex, 1, movingItem);
    } else {
      nextTargetItems.splice(insertIndex, 0, movingItem);
    }

    if (sourceBox === targetBox) {
      setBoxItems(targetBox, nextTargetItems);
    } else {
      setBoxItems(sourceBox, nextSourceItems);
      setBoxItems(targetBox, nextTargetItems);
    }
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDropIndicator(null);
    setTouchPreview(null);
  };

  const getPointerDropTarget = (clientX, clientY) => {
    const element = document.elementFromPoint(clientX, clientY);
    const cardElement = element?.closest("[data-drop-product-id]");
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      const targetId = cardElement.dataset.dropProductId;
      const isSpacerTarget = String(targetId).startsWith("spacer-");
      return {
        boxName: cardElement.dataset.dropBox,
        targetId,
        position: isSpacerTarget
          ? "replace"
          : clientX < rect.left + rect.width / 2
            ? "before"
            : "after",
      };
    }

    const boxElement = element?.closest("[data-drop-box]");
    return boxElement
      ? { boxName: boxElement.dataset.dropBox, targetId: null, position: "after" }
      : null;
  };

  const handlePointerDown = (e, item, boxName) => {
    pointerDragRef.current = {
      id: String(item.productId ?? item.spacerId),
      boxName,
      item,
      startX: e.clientX,
      startY: e.clientY,
      pointerType: e.pointerType,
      dragging: false,
    };
  };

  const handlePointerMove = (e) => {
    const pointerDrag = pointerDragRef.current;
    if (!pointerDrag) return;

    const deltaX = e.clientX - pointerDrag.startX;
    const deltaY = e.clientY - pointerDrag.startY;
    const distance = Math.hypot(deltaX, deltaY);
    if (!pointerDrag.dragging && distance < 6) return;

    if (
      !pointerDrag.dragging &&
      pointerDrag.pointerType !== "mouse" &&
      Math.abs(deltaY) > Math.abs(deltaX)
    ) {
      pointerDragRef.current = null;
      return;
    }

    if (!pointerDrag.dragging) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    pointerDrag.dragging = true;
    e.preventDefault();
    setDraggedCard({
      id: pointerDrag.id,
      boxName: pointerDrag.boxName,
      item: pointerDrag.item,
    });
    setTouchPreview({ item: pointerDrag.item, x: e.clientX, y: e.clientY });

    const target = getPointerDropTarget(e.clientX, e.clientY);
    setDropIndicator(target);
  };

  const handlePointerUp = (e) => {
    const pointerDrag = pointerDragRef.current;
    pointerDragRef.current = null;
    if (!pointerDrag?.dragging) return;

    e.preventDefault();
    const target = getPointerDropTarget(e.clientX, e.clientY);
    const droppedOnItself =
      target?.boxName === pointerDrag.boxName &&
      String(target?.targetId) === String(pointerDrag.id);

    if (target?.boxName && !droppedOnItself) {
      moveCard(
        pointerDrag.id,
        pointerDrag.boxName,
        target.boxName,
        target.targetId,
        target.position
      );
    }

    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    handleDragEnd();
  };

  const handlePointerCancel = () => {
    pointerDragRef.current = null;
    handleDragEnd();
  };

  pointerUpHandlerRef.current = handlePointerUp;
  pointerCancelHandlerRef.current = handlePointerCancel;

  useEffect(() => {
    const finishPointerDrag = (e) => pointerUpHandlerRef.current?.(e);
    const cancelPointerDrag = () => pointerCancelHandlerRef.current?.();

    window.addEventListener("pointerup", finishPointerDrag);
    window.addEventListener("pointercancel", cancelPointerDrag);
    window.addEventListener("blur", cancelPointerDrag);

    return () => {
      window.removeEventListener("pointerup", finishPointerDrag);
      window.removeEventListener("pointercancel", cancelPointerDrag);
      window.removeEventListener("blur", cancelPointerDrag);
    };
  }, []);

  const renderCard = (item, boxName) => (
    <ProductCard
      item={item}
      boxName={boxName}
      isSelected={selectedCard?.id === String(item.productId ?? item.spacerId)}
      isDragging={
        draggedCard?.id === String(item.productId ?? item.spacerId) && Boolean(touchPreview)
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={() => {
        if (pointerDragRef.current) {
          handlePointerCancel();
        }
      }}
      onClick={() => {
        if (!suppressClickRef.current) {
          handleCardClick(String(item.productId ?? item.spacerId), boxName);
        }
      }}
      onRemove={handleRemoveCard}
    />
  );

  const addSpacerToBox1 = () => {
    spacerSeqRef.current += 1;
    setBox1((items) => [...items, createSpacerItem(`${Date.now()}-${spacerSeqRef.current}`)]);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        gap: "20px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="ค้นหาชื่อสินค้า, รหัสสินค้า, หมวดหมู่"
          style={{
            width: "100%",
            maxWidth: "420px",
            padding: "10px 14px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            outline: "none",
            background: "white",
          }}
        />
        <button
          style={{
            padding: "10px 20px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
          onClick={saveLayoutToAPI}
        >
          บันทึก
        </button>
      </div>

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
        <div
          style={{
            width: "220px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 700, color: "#374151" }}>หมวดหมู่</div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              maxHeight: "calc(100vh - 230px)",
              overflowY: "auto",
            }}
          >
            {availableCategories.map((category) => {
              const isActive = selectedCategory === category;
              const label = category === "all" ? "ทั้งหมด" : category;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: isActive ? "1px solid #2563eb" : "1px solid #d1d5db",
                    background: isActive ? "#dbeafe" : "white",
                    color: isActive ? "#1d4ed8" : "#374151",
                    fontWeight: isActive ? 700 : 500,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: "340px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontWeight: 700, color: "#374151" }}>
            รายการที่จัดตำแหน่งแล้ว
          </div>
          <div
            id="box1"
            data-drop-box="box1"
            style={{
              flex: "none",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "10px",
              padding: "16px",
              background: "#f0f0f0",
              height: "calc(100vh - 230px)",
              minHeight: "320px",
              borderRadius: "10px",
              alignContent: "start",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {visibleBox1.map((item) => {
              const indicator =
                dropIndicator?.boxName === "box1" &&
                String(dropIndicator.targetId) === String(item.productId ?? item.spacerId)
                  ? dropIndicator.position
                  : null;

              return (
                <div
                  key={item.productId ?? item.spacerId}
                  data-drop-product-id={item.productId ?? item.spacerId}
                  data-drop-box="box1"
                  style={{
                    position: "relative",
                    borderLeft: indicator === "before" ? "5px solid #1976d2" : "5px solid transparent",
                    borderRight: indicator === "after" ? "5px solid #1976d2" : "5px solid transparent",
                    paddingLeft: "3px",
                    paddingRight: "3px",
                    marginLeft: "-4px",
                    marginRight: "-4px",
                    borderRadius: "4px",
                  }}
                  >
                  {renderCard(item, "box1")}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addSpacerToBox1}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "10px",
              border: "1px dashed #9ca3af",
              background: "#f9fafb",
              color: "#374151",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            + เพิ่มช่องว่าง
          </button>
        </div>

        <div style={{ width: "340px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ fontWeight: 700, color: "#374151" }}>
            รายการสินค้าที่ไม่มีตำแหน่ง
          </div>
          <div
            id="box2"
            data-drop-box="box2"
            style={{
              flex: "none",
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "10px",
              padding: "16px",
              background: "#e0e0e0",
              height: "calc(100vh - 230px)",
              minHeight: "320px",
              borderRadius: "10px",
              alignContent: "start",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {visibleBox2.map((item) => (
              <React.Fragment key={item.productId}>
                {renderCard(item, "box2")}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {touchPreview && (
        <div
          style={{
            position: "fixed",
            left: touchPreview.x + 14,
            top: touchPreview.y + 14,
            width: "150px",
            minHeight: "76px",
            padding: "12px",
            borderRadius: "8px",
            background: isSpacerItem(touchPreview.item)
              ? "#9ca3af"
              : touchPreview.item.color,
            color: isSpacerItem(touchPreview.item)
              ? "#f3f4f6"
              : getContrastColor(touchPreview.item.color),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            textAlign: "center",
            fontWeight: "bold",
            pointerEvents: "none",
            zIndex: 9999,
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.3)",
          }}
        >
          {isSpacerItem(touchPreview.item) ? (
            <div style={{ fontSize: "14px", fontWeight: 700 }}>ช่องว่าง</div>
          ) : (
            <>
              <div>{touchPreview.item.title}</div>
              <div style={{ fontSize: "12px", fontWeight: 500, opacity: 0.9 }}>
                {touchPreview.item.code
                  ? `รหัสสินค้า: ${touchPreview.item.code}`
                  : " "}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
